/**
 * Query key factories — one per domain.
 *
 * Every key includes the tenant ID so cached data is never shared across
 * tenants when a user switches accounts.
 */
import { getActiveTenantQueryKey } from './activeTenant';

function getTenantId(): string {
  return getActiveTenantQueryKey();
}

// ─── Shared param shapes ──────────────────────────────────────────────────────

export type ListParams = object;

export const formsEngineKeys = {
  all: () => ['forms-engine', getTenantId()] as const,
  templates: (params: ListParams = {}) => [...formsEngineKeys.all(), 'templates', params] as const,
  template: (id: string) => [...formsEngineKeys.all(), 'templates', id] as const,
  templateVersions: (id: string) => [...formsEngineKeys.template(id), 'versions'] as const,
  templateImpact: (id: string) => [...formsEngineKeys.template(id), 'impact'] as const,
  intakePublicLink: (id: string) => [...formsEngineKeys.template(id), 'public-link'] as const,
  questions: (params: ListParams = {}) => [...formsEngineKeys.all(), 'questions', params] as const,
  question: (id: string) => [...formsEngineKeys.all(), 'questions', id] as const,
  sections: (params: ListParams = {}) => [...formsEngineKeys.all(), 'sections', params] as const,
  section: (id: string) => [...formsEngineKeys.all(), 'sections', id] as const,
  sets: () => [...formsEngineKeys.all(), 'sets'] as const,
  set: (id: string) => [...formsEngineKeys.sets(), id] as const,
  instances: (subjectType: string, subjectId: string, surface?: string) =>
    [...formsEngineKeys.all(), 'instances', subjectType, subjectId, surface ?? 'all'] as const,
  // Activity tab (Task 11) — exceptions for one intake template, and the
  // cross-template summary that feeds the Forms library badges.
  intakeActivity: (templateId: string) =>
    [...formsEngineKeys.all(), 'intake-activity', templateId] as const,
  intakeActivitySummary: () => [...formsEngineKeys.all(), 'intake-activity-summary'] as const,
  // Every submission of one intake template, cursor-paged, for the same tab;
  // with a status, only those (the pinned spam section). The bare key is a
  // prefix of the status one, so invalidating it refreshes both lists.
  intakeSubmissions: (templateId: string, status?: 'SPAM') =>
    [
      ...formsEngineKeys.all(),
      'intake-submissions',
      templateId,
      ...(status ? [status] : []),
    ] as const,
};

// ─── Contacts ─────────────────────────────────────────────────────────────────

export const locationKeys = {
  all: () => ['locations', getTenantId()] as const,
  lists: () => [...locationKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...locationKeys.lists(), params] as const,
  details: () => [...locationKeys.all(), 'detail'] as const,
  detail: (id: string) => [...locationKeys.details(), id] as const,
  equipment: (id: string) => [...locationKeys.detail(id), 'equipment'] as const,
};

export const contactKeys = {
  all: () => ['contacts', getTenantId()] as const,
  lists: () => [...contactKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...contactKeys.lists(), params] as const,
  relationships: (accountId: string, viewedContactId: string) =>
    [...contactKeys.lists(), 'relationships', accountId, viewedContactId] as const,
  search: (query: string, limit: number) => [...contactKeys.all(), 'search', query, limit] as const,
  importJob: (jobId: string) => [...contactKeys.all(), 'import-job', jobId] as const,
  details: () => [...contactKeys.all(), 'detail'] as const,
  /**
   * Every location linked to this contact. Under `all()` on purpose: a
   * hand-rolled `['contacts', contactId, …]` key sits outside every existing
   * invalidation, so creating a location left this list showing the old answer
   * until the page was reloaded.
   */
  locations: (contactId: string) => [...contactKeys.all(), 'locations', contactId] as const,
  detail: (id: string) => [...contactKeys.details(), id] as const,
  summary: (id: string) => [...contactKeys.detail(id), 'summary'] as const,
  addresses: (id: string) => [...contactKeys.detail(id), 'addresses'] as const,
  billTo: (id: string) => [...contactKeys.detail(id), 'billTo'] as const,
  notes: (id: string) => [...contactKeys.detail(id), 'notes'] as const,
  files: (id: string) => [...contactKeys.detail(id), 'files'] as const,
  communicationsBase: (id: string) => [...contactKeys.detail(id), 'communications'] as const,
  communications: (id: string, params: ListParams = {}) =>
    [...contactKeys.communicationsBase(id), params] as const,
  jobsBase: (id: string) => [...contactKeys.detail(id), 'jobs'] as const,
  jobs: (id: string, params: ListParams = {}) => [...contactKeys.jobsBase(id), params] as const,
  // NOT-573 media roll-up (params: jobId / tag / from / to filters).
  mediaBase: (id: string) => [...contactKeys.detail(id), 'media'] as const,
  media: (id: string, params: ListParams = {}) => [...contactKeys.mediaBase(id), params] as const,
  estimatesBase: (id: string) => [...contactKeys.detail(id), 'estimates'] as const,
  estimates: (id: string, params: ListParams = {}) =>
    [...contactKeys.estimatesBase(id), params] as const,
  invoicesBase: (id: string) => [...contactKeys.detail(id), 'invoices'] as const,
  invoices: (id: string, params: ListParams = {}) =>
    [...contactKeys.invoicesBase(id), params] as const,
  paymentsBase: (id: string) => [...contactKeys.detail(id), 'payments'] as const,
  payments: (id: string, params: ListParams = {}) =>
    [...contactKeys.paymentsBase(id), params] as const,
  financialActivityBase: (id: string) => [...contactKeys.detail(id), 'financial-activity'] as const,
  financialActivity: (id: string, limit = 50) =>
    [...contactKeys.financialActivityBase(id), limit] as const,
  membershipsBase: (id: string) => [...contactKeys.detail(id), 'memberships'] as const,
  memberships: (id: string, params: ListParams = {}) =>
    [...contactKeys.membershipsBase(id), params] as const,
  membershipActive: (id: string) => [...contactKeys.membershipsBase(id), 'active'] as const,
  membershipBillingHistoryBase: (id: string, membershipId: string) =>
    [...contactKeys.membershipsBase(id), membershipId, 'billing-history'] as const,
  membershipBillingHistory: (id: string, membershipId: string, params: ListParams = {}) =>
    [...contactKeys.membershipBillingHistoryBase(id, membershipId), params] as const,
  membershipOpportunity: (id: string) =>
    [...contactKeys.membershipsBase(id), 'opportunity'] as const,
  statuses: () => [...contactKeys.all(), 'statuses'] as const,
  paymentMethods: (id: string) => [...contactKeys.detail(id), 'payment-methods'] as const,
  /**
   * One Stripe SetupIntent per collector + rail + attempt (`requestKey`).
   * Deliberately NOT under `detail(id)`: every payment mutation invalidates
   * that prefix, and a refetch here would ask the server for the intent again.
   */
  paymentMethodSetupIntent: (id: string, rail: string, requestKey: string) =>
    ['contact-payment-method-setup-intent', getTenantId(), id, rail, requestKey] as const,
};

// ─── Equipment (Equipment Registry & Deployment — operations) ─────────────────

export const equipmentKeys = {
  all: () => ['equipment', getTenantId()] as const,
  lists: () => [...equipmentKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...equipmentKeys.lists(), params] as const,
  byCustomer: (customerId: string) => [...equipmentKeys.all(), 'customer', customerId] as const,
  details: () => [...equipmentKeys.all(), 'detail'] as const,
  detail: (id: string) => [...equipmentKeys.details(), id] as const,
  workHistory: (id: string) => [...equipmentKeys.detail(id), 'work-history'] as const,
  categories: () => [...equipmentKeys.all(), 'categories'] as const,
  categoriesAvailable: () => [...equipmentKeys.all(), 'categories', 'available'] as const,
  descriptions: (params: ListParams = {}) =>
    [...equipmentKeys.all(), 'descriptions', params] as const,
  settings: () => [...equipmentKeys.all(), 'settings'] as const,
  warrantyExpiringAll: () => [...equipmentKeys.all(), 'warranty-expiring'] as const,
  warrantyExpiring: (days?: number) =>
    [...equipmentKeys.warrantyExpiringAll(), days ?? 'default'] as const,
  maintenanceOverdue: () => [...equipmentKeys.all(), 'maintenance-overdue'] as const,
  reports: () => [...equipmentKeys.all(), 'reports'] as const,
  report: (name: string) => [...equipmentKeys.reports(), name] as const,
};

// ─── Bookings ─────────────────────────────────────────────────────────────────

export const bookingKeys = {
  all: () => ['bookings', getTenantId()] as const,
  lists: () => [...bookingKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...bookingKeys.lists(), params] as const,
  details: () => [...bookingKeys.all(), 'detail'] as const,
  detail: (id: string) => [...bookingKeys.details(), id] as const,
};

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const invoiceKeys = {
  all: () => ['invoices', getTenantId()] as const,
  lists: () => [...invoiceKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...invoiceKeys.lists(), params] as const,
  byJobPrefix: (jobId: string) => [...invoiceKeys.all(), 'job', jobId] as const,
  byJob: (jobId: string, params: ListParams = {}) =>
    [...invoiceKeys.byJobPrefix(jobId), params] as const,
  createPreview: (jobId: string) => [...invoiceKeys.byJobPrefix(jobId), 'create-preview'] as const,
  details: () => [...invoiceKeys.all(), 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  attachments: (id: string) => [...invoiceKeys.detail(id), 'attachments'] as const,
};

// ─── Payments ────────────────────────────────────────────────────────────────

export const paymentKeys = {
  all: () => ['payments', getTenantId()] as const,
  lists: () => [...paymentKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...paymentKeys.lists(), params] as const,
  details: () => [...paymentKeys.all(), 'detail'] as const,
  detail: (id: string | undefined) => [...paymentKeys.details(), id] as const,
  // Card service-fee quote for a charge the operator is composing. Keyed on
  // every input the server prices on, so changing the amount or the saved
  // method refetches instead of showing the previous instrument's fee.
  serviceFeeQuote: (params: ListParams = {}) =>
    [...paymentKeys.all(), 'service-fee-quote', params] as const,
  refunds: (paymentId: string | undefined) => [...paymentKeys.all(), 'refunds', paymentId] as const,
  /** Whether this tenant can scan a check right now (setting + Payabli). */
  chequeScanAvailability: () => [...paymentKeys.all(), 'cheque-scan-availability'] as const,
};

// ─── Receipts ────────────────────────────────────────────────────────────────

export const receiptKeys = {
  all: () => ['receipts', getTenantId()] as const,
  lists: () => [...receiptKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...receiptKeys.lists(), params] as const,
  details: () => [...receiptKeys.all(), 'detail'] as const,
  detail: (id: string | undefined) => [...receiptKeys.details(), id] as const,
  byPayment: (paymentId: string | undefined) =>
    [...receiptKeys.all(), 'by-payment', paymentId] as const,
  byDeposit: (depositId: string | undefined) =>
    [...receiptKeys.all(), 'by-deposit', depositId] as const,
};

// ─── Credit Memos (spec §8) ───────────────────────────────────────────────────

export const creditMemoKeys = {
  all: () => ['creditMemos', getTenantId()] as const,
  lists: () => [...creditMemoKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...creditMemoKeys.lists(), params] as const,
  details: () => [...creditMemoKeys.all(), 'detail'] as const,
  detail: (id: string) => [...creditMemoKeys.details(), id] as const,
  autoSuggest: (contactId: string, invoiceId?: string) =>
    [...creditMemoKeys.all(), 'autoSuggest', contactId, invoiceId ?? 'no-invoice'] as const,
};

// ─── Notes ───────────────────────────────────────────────────────────────────

export const noteKeys = {
  all: () => ['notes', getTenantId()] as const,
  entity: (entityType: string, entityId: string) =>
    [...noteKeys.all(), entityType, entityId] as const,
};

// ─── Change Orders ───────────────────────────────────────────────────────────

export const changeOrderKeys = {
  all: () => ['changeOrders', getTenantId()] as const,
  byJob: (jobId: string, params: ListParams = {}) =>
    [...changeOrderKeys.all(), 'job', jobId, params] as const,
  scopeHistory: (jobId: string) => [...changeOrderKeys.all(), 'scopeHistory', jobId] as const,
  impactPreview: (jobId: string, scope: string) =>
    [...changeOrderKeys.all(), 'impactPreview', jobId, scope] as const,
  details: () => [...changeOrderKeys.all(), 'detail'] as const,
  detail: (id: string) => [...changeOrderKeys.details(), id] as const,
};

// ─── Progress Payments ──────────────────────────────────────────────────────

export const progressPaymentKeys = {
  all: () => ['progressPayments', getTenantId()] as const,
  byJob: (jobId: string) => [...progressPaymentKeys.all(), 'job', jobId] as const,
};

// ─── Projects (SPEC-projects) ───────────────────────────────────────────────

export const projectKeys = {
  all: () => ['projects', getTenantId()] as const,
  lists: () => [...projectKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...projectKeys.lists(), params] as const,
  details: () => [...projectKeys.all(), 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  summary: (id: string) => [...projectKeys.detail(id), 'summary'] as const,
  entities: (id: string) => [...projectKeys.detail(id), 'entities'] as const,
  contract: (id: string) => [...projectKeys.detail(id), 'contract'] as const,
  payApplications: (id: string) => [...projectKeys.detail(id), 'pay-applications'] as const,
  payApplication: (appId: string) => [...projectKeys.all(), 'pay-application', appId] as const,
  lienWaivers: (id: string) => [...projectKeys.detail(id), 'lien-waivers'] as const,
  dependencies: (id: string) => [...projectKeys.detail(id), 'dependencies'] as const,
  attachments: (id: string) => [...projectKeys.detail(id), 'attachments'] as const,
  attachableEstimates: (id: string) => [...projectKeys.detail(id), 'attachable-estimates'] as const,
};

// ─── Deposits (Wave 2 — spec §4) ────────────────────────────────────────────

export const depositKeys = {
  all: () => ['deposits', getTenantId()] as const,
  lists: () => [...depositKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...depositKeys.lists(), params] as const,
  details: () => [...depositKeys.all(), 'detail'] as const,
  detail: (id: string) => [...depositKeys.details(), id] as const,
  contactBalance: (contactId: string) =>
    [...depositKeys.all(), 'contactBalance', contactId] as const,
};

// ─── Recurring Billing ──────────────────────────────────────────────────────

export const recurringBillingKeys = {
  all: () => ['recurringBilling', getTenantId()] as const,
  plans: () => [...recurringBillingKeys.all(), 'plans'] as const,
  planList: (params: ListParams = {}) => [...recurringBillingKeys.plans(), 'list', params] as const,
  planDetail: (id: string) => [...recurringBillingKeys.plans(), 'detail', id] as const,
  runs: () => [...recurringBillingKeys.all(), 'runs'] as const,
  runList: (params: ListParams = {}) => [...recurringBillingKeys.runs(), 'list', params] as const,
  runDetail: (id: string) => [...recurringBillingKeys.runs(), 'detail', id] as const,
  exceptions: (params: ListParams = {}) =>
    [...recurringBillingKeys.all(), 'exceptions', params] as const,
};

export const officeRecoveryRenewalKeys = {
  all: () => ['officeRecoveryRenewal', getTenantId()] as const,
  recovery: (params: ListParams = {}) =>
    [...officeRecoveryRenewalKeys.all(), 'recovery', params] as const,
  renewals: (params: ListParams = {}) =>
    [...officeRecoveryRenewalKeys.all(), 'renewals', params] as const,
};

// ─── Proposals ────────────────────────────────────────────────────────────────

export const proposalKeys = {
  all: () => ['proposals', getTenantId()] as const,
  lists: () => [...proposalKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...proposalKeys.lists(), params] as const,
  details: () => [...proposalKeys.all(), 'detail'] as const,
  detail: (id: string) => [...proposalKeys.details(), id] as const,
  attachments: (id: string) => [...proposalKeys.detail(id), 'attachments'] as const,
};

// ─── Leads ────────────────────────────────────────────────────────────────────

export const arReportKeys = {
  all: () => ['ar-reports', getTenantId()] as const,
  report: (name: string, period?: { from?: string; to?: string; ownerId?: string }) =>
    [
      ...arReportKeys.all(),
      name,
      period?.from ?? '',
      period?.to ?? '',
      period?.ownerId ?? '',
    ] as const,
};

// NOT-542 — Divisional P&L by Business Unit.
export const pnlReportKeys = {
  all: () => ['pnl-reports', getTenantId()] as const,
  report: (name: string, period?: { from?: string; to?: string }) =>
    [...pnlReportKeys.all(), name, period?.from ?? '', period?.to ?? ''] as const,
};

export const technicianReportKeys = {
  all: () => ['technician-reports', getTenantId()] as const,
  report: (name: string, period?: { from?: string; to?: string; technicianId?: string }) =>
    [
      ...technicianReportKeys.all(),
      name,
      period?.from ?? '',
      period?.to ?? '',
      period?.technicianId ?? '',
    ] as const,
};

export const customerReportKeys = {
  all: () => ['customer-reports', getTenantId()] as const,
  report: (name: string, period?: { from?: string; to?: string }) =>
    [...customerReportKeys.all(), name, period?.from ?? '', period?.to ?? ''] as const,
};

export const executiveReportKeys = {
  all: () => ['executive-reports', getTenantId()] as const,
  report: (name: string, period?: { from?: string; to?: string }) =>
    [...executiveReportKeys.all(), name, period?.from ?? '', period?.to ?? ''] as const,
};

export const operationalInsightKeys = {
  all: () => ['operational-insights', getTenantId()] as const,
  report: (name: string, period?: { from?: string; to?: string }) =>
    [...operationalInsightKeys.all(), name, period?.from ?? '', period?.to ?? ''] as const,
};

export const targetKeys = {
  all: () => ['report-targets', getTenantId()] as const,
  list: (scope = 'TENANT', scopeId = '', periodStart = '') =>
    [...targetKeys.all(), 'list', scope, scopeId, periodStart] as const,
  attainment: (scope = 'TENANT', scopeId = '', reference = '') =>
    [...targetKeys.all(), 'attainment', scope, scopeId, reference] as const,
};

export const leadKeys = {
  all: () => ['leads', getTenantId()] as const,
  lists: () => [...leadKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...leadKeys.lists(), params] as const,
  details: () => [...leadKeys.all(), 'detail'] as const,
  detail: (id: string) => [...leadKeys.details(), id] as const,
  // A lead's intake form submissions (Form tab). Nested under detail() so it
  // shares the lead's own cache-invalidation namespace.
  formSubmissions: (leadId: string) => [...leadKeys.detail(leadId), 'form-submissions'] as const,
  stages: () => [...leadKeys.all(), 'stages'] as const,
  pipeline: () => [...leadKeys.all(), 'pipeline'] as const,
  // Filtered pipeline aggregate (board per-column counts/$). Nested under
  // pipeline() so existing `invalidateQueries(pipeline())` refreshes every
  // filter variant after a stage move / assignment.
  pipelineSummary: (params: ListParams = {}) => [...leadKeys.pipeline(), params] as const,
  // One paginated card query per board column (the mounted observer). Nested
  // under lists() so the stage-move mutation's `invalidateQueries(lists())`
  // refreshes the cards too.
  boardColumn: (stageId: string, params: ListParams, pageCount: number) =>
    [...leadKeys.lists(), 'board-column', stageId, params, pageCount] as const,
  // A single cached page for a board column. The aggregate boardColumn query
  // pulls each page through `ensureQueryData` on this key, so "Load more" only
  // fetches the new page (prior pages are cache hits) instead of re-fetching
  // the whole 1..N prefix. Also under lists() so mutations refresh it.
  boardColumnPage: (stageId: string, params: ListParams, page: number) =>
    [...leadKeys.lists(), 'board-column-page', stageId, params, page] as const,
  settings: () => [...leadKeys.all(), 'settings'] as const,
  assignableUsers: (includeUserId?: string) =>
    [...leadKeys.all(), 'assignable-users', includeUserId ?? null] as const,
  assignableTeams: () => [...leadKeys.all(), 'assignable-teams'] as const,
  assignableOwners: () => [...leadKeys.all(), 'assignable-owners'] as const,
  savedViews: () => [...leadKeys.all(), 'saved-views'] as const,
  reports: () => [...leadKeys.all(), 'reports'] as const,
  report: (name: string, period?: { from?: string; to?: string; ownerId?: string }) =>
    [
      ...leadKeys.reports(),
      name,
      period?.from ?? '',
      period?.to ?? '',
      period?.ownerId ?? '',
    ] as const,
};

// ─── Acquisition Channels ─────────────────────────────────────────────────────

export const acquisitionChannelKeys = {
  all: () => ['acquisition-channels', getTenantId()] as const,
};

export const leadRepAvailabilityKeys = {
  all: () => ['leadRepAvailability', getTenantId()] as const,
  list: () => [...leadRepAvailabilityKeys.all(), 'list'] as const,
  schedule: (userId: string) => [...leadRepAvailabilityKeys.all(), 'schedule', userId] as const,
};

// ─── Crew ─────────────────────────────────────────────────────────────────────

export const crewKeys = {
  all: () => ['crew', getTenantId()] as const,
  lists: () => [...crewKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...crewKeys.lists(), params] as const,
  details: () => [...crewKeys.all(), 'detail'] as const,
  detail: (id: string) => [...crewKeys.details(), id] as const,
  locations: () => [...crewKeys.all(), 'locations'] as const,
  skillsCatalog: () => [...crewKeys.all(), 'skills-catalog'] as const,
};

// ─── Tasks ────────────────────────────────────────────────────────────────────

export const taskKeys = {
  all: () => ['tasks', getTenantId()] as const,
  lists: () => [...taskKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...taskKeys.lists(), params] as const,
  // Not under lists(): optimistic status updates rewrite every lists() cache as
  // a TaskListResponse. Every task mutation must invalidate countsAll()
  // alongside lists() (or all()), or the Tasks tab badges go stale.
  countsAll: () => [...taskKeys.all(), 'counts'] as const,
  counts: (params: ListParams) => [...taskKeys.countsAll(), params] as const,
  details: () => [...taskKeys.all(), 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
};

// ─── Pricebook ────────────────────────────────────────────────────────────────

export const pricebookKeys = {
  all: () => ['pricebook', getTenantId()] as const,
  lists: () => [...pricebookKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...pricebookKeys.lists(), params] as const,
  details: () => [...pricebookKeys.all(), 'detail'] as const,
  detail: (id: string) => [...pricebookKeys.details(), id] as const,
  usage: (id: string) => [...pricebookKeys.detail(id), 'usage'] as const,
  /** NOT-590 — the materials/equipment a SERVICE includes. */
  components: (id: string) => [...pricebookKeys.detail(id), 'components'] as const,
  categories: () => [...pricebookKeys.all(), 'categories'] as const,
  /** Browse tree, per catalog family. */
  categoryTree: (params?: Record<string, unknown>) =>
    [...pricebookKeys.categories(), 'tree', params ?? {}] as const,
  /** Items filed inside one folder. */
  categoryItems: (id: string, params?: Record<string, unknown>) =>
    [...pricebookKeys.categories(), id, 'items', params ?? {}] as const,
  /** An item's category memberships. */
  itemCategories: (lineItemId: string) =>
    [...pricebookKeys.detail(lineItemId), 'categories'] as const,
};

// ─── Reviews ──────────────────────────────────────────────────────────────────

/**
 * Review keys cover the v2 unified Reviews Dashboard (`/v2/reviews` +
 * `/v2/review-platforms/replies/...`). Detail keys take a composite source +
 * raw id pair so cached entries don't collide across the three underlying
 * tables (ExternalReview / ReviewResponse / Testimonial).
 */
export const reviewKeys = {
  all: () => ['reviews', getTenantId()] as const,
  lists: () => [...reviewKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...reviewKeys.lists(), params] as const,
  details: () => [...reviewKeys.all(), 'detail'] as const,
  /**
   * Drafts auto-post would publish, with the reason for any it will not.
   *
   * Keyed by `locationId` because the endpoint is scoped by it. Without that,
   * switching location serves the previous location's cached drafts under the
   * new heading — and the cancel button on those rows would act on reviews the
   * screen is no longer claiming to show.
   */
  autoPostPending: (locationId: string | null = null) =>
    [...reviewKeys.all(), 'auto-post', 'pending', locationId] as const,
  /** Detail key — `source` matches the route segment on the backend. */
  detail: (source: 'response' | 'external' | 'testimonial', id: string) =>
    [...reviewKeys.details(), source, id] as const,
  /**
   * Param-free prefix, so a mutation can invalidate every stats window at once.
   * `stats({ windowDays })` embeds its params, and TanStack matches by prefix —
   * invalidating `stats()` with no argument would look for a key ending in
   * `undefined` and match nothing.
   */
  statsAll: () => [...reviewKeys.all(), 'stats'] as const,
  stats: (params?: Record<string, unknown>) => [...reviewKeys.statsAll(), params] as const,
  templates: () => [...reviewKeys.all(), 'templates'] as const,
  /**
   * The window is the whole question, so it belongs in the key. The endpoint
   * refuses to default `from`/`to` on the grounds that a baseline whose window
   * depends on when it ran is not a baseline — two windows are two different
   * answers and must not share a cache entry.
   */
  funnel: (params: { from: string; to: string }) =>
    [...reviewKeys.all(), 'funnel', params] as const,
};

// ─── Reminders Queue (operations v2, spec §13) ────────────────────────────────

export const reminderKeys = {
  all: () => ['reminders-v2', getTenantId()] as const,
  lists: () => [...reminderKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...reminderKeys.lists(), params] as const,
  details: () => [...reminderKeys.all(), 'detail'] as const,
  detail: (id: string) => [...reminderKeys.details(), id] as const,
  previewNextStep: (id: string) => [...reminderKeys.detail(id), 'preview-next-step'] as const,
  auditLog: (id: string) => [...reminderKeys.detail(id), 'audit-log'] as const,
};

// ─── Timesheet ────────────────────────────────────────────────────────────────

export const timesheetKeys = {
  all: () => ['timesheet', getTenantId()] as const,
  periods: () => [...timesheetKeys.all(), 'period'] as const,
  period: (params: { periodStart: string; periodEnd: string; technicianId?: string }) =>
    [...timesheetKeys.periods(), params] as const,
  dayEntries: (technicianId: string, date: string) =>
    [...timesheetKeys.all(), 'day', technicianId, date] as const,
  openSessions: () => [...timesheetKeys.all(), 'openSessions'] as const,
};

// ─── Appointment Forms ───────────────────────────────────────────────────────

export const formDefinitionKeys = {
  all: () => ['appointmentFormDefinitions', getTenantId()] as const,
  lists: () => [...formDefinitionKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...formDefinitionKeys.lists(), params] as const,
  byAppointment: (appointmentId: string) =>
    [...formDefinitionKeys.all(), 'appointment', appointmentId] as const,
  details: () => [...formDefinitionKeys.all(), 'detail'] as const,
  detail: (id: string) => [...formDefinitionKeys.details(), id] as const,
};

export const formSubmissionKeys = {
  all: () => ['appointmentFormSubmissions', getTenantId()] as const,
  byAppointment: (appointmentId: string) =>
    [...formSubmissionKeys.all(), 'appointment', appointmentId] as const,
  detail: (id: string) => [...formSubmissionKeys.all(), 'detail', id] as const,
};

// ─── Opportunities (removed — replaced by Leads) ─────────────────────────────

// ─── Settings ─────────────────────────────────────────────────────────────────

export const settingsKeys = {
  all: () => ['settings', getTenantId()] as const,
  company: () => [...settingsKeys.all(), 'company'] as const,
  // NOT-236: PDF-only variant that also carries the inline logo data URI.
  // Separate key so the extra S3 fetch isn't shared into the on-screen
  // company-settings cache (which uses `logoUrl`). Deliberately nested UNDER
  // `company()` so any prefix invalidation of the company key also evicts this
  // PDF cache — keeping generated-PDF branding in lock-step with the on-screen
  // company settings after a company/logo edit.
  companyWithLogoDataUri: () => [...settingsKeys.company(), 'with-logo-data-uri'] as const,
  businessSettings: () => [...settingsKeys.all(), 'business-settings'] as const,
  /** PLAT-FF-1 — rollout feature flags evaluated for the tenant. */
  featureFlags: () => [...settingsKeys.all(), 'feature-flags'] as const,
  clientPortal: () => [...settingsKeys.all(), 'client-portal'] as const,
  estimateDocTemplates: (docType?: string) =>
    [...settingsKeys.all(), 'estimate-doc-templates', docType ?? 'ESTIMATE'] as const,
  escalationRules: () => [...settingsKeys.all(), 'escalation-rules'] as const,
  bookableServices: () => [...settingsKeys.all(), 'bookable-services'] as const,
  bookingLinks: () => [...settingsKeys.all(), 'booking-links'] as const,
  shortLinks: () => [...settingsKeys.all(), 'short-links'] as const,
  partnerApiKeys: () => [...settingsKeys.all(), 'partner-api-keys'] as const,
  users: () => [...settingsKeys.all(), 'users'] as const,
  allUsers: () => [...settingsKeys.users(), 'all-pages'] as const,
  // NOT-509: nested under users() so the existing user/role mutations, which
  // invalidate settingsKeys.users() (prefix match), also refresh the eligibility
  // list — a demoted/promoted user can't linger in the salesperson picker.
  // `includeUserId` widens the list by one lead rep (NOT-584), so it has to be
  // part of the key — otherwise the first lead's roster is served to every
  // other lead from cache.
  salesAttributionCandidates: (includeUserId?: string) =>
    [...settingsKeys.users(), 'sales-attribution-candidates', includeUserId ?? null] as const,
  userDetail: (id: string) => [...settingsKeys.users(), id] as const,
  userGroups: (id: string) => [...settingsKeys.userDetail(id), 'groups'] as const,
  roles: () => [...settingsKeys.all(), 'roles'] as const,
  roleDetail: (id: string) => [...settingsKeys.roles(), id] as const,
  groups: () => [...settingsKeys.all(), 'groups'] as const,
  groupDetail: (id: string) => [...settingsKeys.groups(), id] as const,
  permissionCatalog: () => [...settingsKeys.all(), 'permission-catalog'] as const,
  notificationPreferences: () => [...settingsKeys.all(), 'notification-preferences'] as const,
  operationalNotificationRules: () =>
    [...settingsKeys.all(), 'operational-notification-rules'] as const,
  billing: () => [...settingsKeys.all(), 'billing'] as const,
  quickbooks: () => [...settingsKeys.all(), 'quickbooks'] as const,
  qbFieldMapping: () => [...settingsKeys.all(), 'quickbooks', 'field-mapping'] as const,
  qbAccountConfig: () => [...settingsKeys.all(), 'quickbooks', 'account-config'] as const,
  qbAccounts: () => [...settingsKeys.all(), 'quickbooks', 'accounts'] as const,
  qbClasses: () => [...settingsKeys.all(), 'quickbooks', 'classes'] as const,
  qbPaymentMethods: () => [...settingsKeys.all(), 'quickbooks', 'payment-methods'] as const,
  accounting: () => [...settingsKeys.all(), 'accounting'] as const,
  accountingDashboard: () => [...settingsKeys.accounting(), 'dashboard'] as const,
  revenueRecognition: () => [...settingsKeys.accounting(), 'revenue-recognition'] as const,
  accountingFailures: () => [...settingsKeys.accounting(), 'failures'] as const,
  accountingQueueDepth: () => [...settingsKeys.accounting(), 'queue-depth'] as const,
  accountingHistory: () => [...settingsKeys.accounting(), 'history'] as const,
  accountingEntity: (type: string, id: string) =>
    [...settingsKeys.accounting(), 'entity', type, id] as const,
  accountingExclusions: () => [...settingsKeys.accounting(), 'exclusions'] as const,
  accountingConflicts: () => [...settingsKeys.accounting(), 'conflicts'] as const,
  accountingConflictDiff: (logId: string) =>
    [...settingsKeys.accounting(), 'conflicts', 'diff', logId] as const,
  // QBO reconciliation (spec §9.6 / PR 8)
  qboReconciliationLatest: () =>
    [...settingsKeys.all(), 'qbo', 'reconciliation', 'latest'] as const,
  qboReconciliationReport: (reportId: string) =>
    [...settingsKeys.all(), 'qbo', 'reconciliation', 'report', reportId] as const,
  qboDeadLetter: () => [...settingsKeys.all(), 'qbo', 'dead-letter'] as const,
  taxes: () => [...settingsKeys.all(), 'taxes'] as const,
  customFields: () => [...settingsKeys.all(), 'custom-fields'] as const,
  jobTypes: () => [...settingsKeys.all(), 'job-types'] as const,
  checklists: () => [...settingsKeys.all(), 'checklists'] as const,
  checklistTemplate: (id: string) => [...settingsKeys.all(), 'checklists', id] as const,
  checklistTemplateVersions: (id: string) =>
    [...settingsKeys.all(), 'checklists', id, 'versions'] as const,
  assets: () => [...settingsKeys.all(), 'settings-assets'] as const,
  chatbot: () => [...settingsKeys.all(), 'chatbot'] as const,
  zapierConnections: () => [...settingsKeys.all(), 'zapier', 'connections'] as const,
  zapierConfig: () => [...settingsKeys.all(), 'zapier', 'config'] as const,
  payments: () => [...settingsKeys.all(), 'payments'] as const,
  stripeAccountSession: () => [...settingsKeys.payments(), 'stripe-account-session'] as const,
  payabliClientConfig: () => [...settingsKeys.payments(), 'payabli-client-config'] as const,
  payabliConnection: () => [...settingsKeys.payments(), 'payabli-connect'] as const,
  estimateSettings: () => [...settingsKeys.all(), 'estimate-settings'] as const,
  wisetackSettings: () => [...settingsKeys.all(), 'wisetack-settings'] as const,
  termsTemplates: () => [...settingsKeys.all(), 'terms-templates'] as const,
  scopeOfWorkTemplates: () => [...settingsKeys.all(), 'scope-of-work-templates'] as const,
  quoteCustomization: () => [...settingsKeys.all(), 'quote-customization'] as const,
  // PR 9 — unified Billing settings panel
  billingSettings: () => [...settingsKeys.all(), 'billing-settings'] as const,
};

export const notificationKeys = {
  all: () => ['notifications', getTenantId()] as const,
  lists: () => [...notificationKeys.all(), 'list'] as const,
  list: (filters: {
    unreadOnly?: boolean;
    category?: string;
    priority?: string;
    eventType?: string;
    excludeEventType?: string;
    status?: string;
    limit?: number;
  }) => [...notificationKeys.lists(), filters] as const,
  groupItemsAll: () => [...notificationKeys.all(), 'group-items'] as const,
  groupItems: (
    id: string,
    status: string = 'current',
    firstSeenAt?: string,
    lastEventAt?: string,
  ) => [...notificationKeys.all(), 'group-items', id, status, firstSeenAt, lastEventAt] as const,
  unreadCount: () => [...notificationKeys.all(), 'unread-count'] as const,
};

// ─── Checklist Instances (appointment-scoped) ─────────────────────────────────

export const checklistInstanceKeys = {
  all: () => ['checklistInstances', getTenantId()] as const,
  byAppointment: (appointmentId: string) =>
    [...checklistInstanceKeys.all(), 'appointment', appointmentId] as const,
  snapshot: (appointmentId: string) =>
    [...checklistInstanceKeys.byAppointment(appointmentId), 'snapshot'] as const,
  jobReports: (jobId: string) => [...checklistInstanceKeys.all(), 'jobReports', jobId] as const,
  detail: (appointmentId: string, instanceId: string) =>
    [...checklistInstanceKeys.byAppointment(appointmentId), instanceId] as const,
};

// ─── Scheduler ────────────────────────────────────────────────────────────────

export const schedulerKeys = {
  all: () => ['scheduler', getTenantId()] as const,
  availability: (date?: string) => [...schedulerKeys.all(), 'availability', date ?? 'all'] as const,
  crewAvailability: (crewId: string) =>
    [...schedulerKeys.all(), 'crewAvailability', crewId] as const,
  conflicts: () => [...schedulerKeys.all(), 'conflicts'] as const,
  // Debounced pre-save conflict check, keyed by preflightRequestKey() (sorted techs + time + context).
  conflictPreflight: (requestKey: string) =>
    [...schedulerKeys.conflicts(), 'preflight', requestKey] as const,
  lists: () => [...schedulerKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...schedulerKeys.lists(), params] as const,
  details: () => [...schedulerKeys.all(), 'detail'] as const,
  detail: (id: string) => [...schedulerKeys.details(), id] as const,
  bookingLists: () => [...schedulerKeys.all(), 'bookings'] as const,
  bookings: (params?: ListParams) => [...schedulerKeys.bookingLists(), params] as const,
  booking: (id: string) => [...schedulerKeys.all(), 'booking', id] as const,
  bookingAvailability: (params?: ListParams) =>
    [...schedulerKeys.all(), 'bookingAvailability', params] as const,
  technicians: () => [...schedulerKeys.all(), 'technicians'] as const,
  // GAP-02 — a technician's upcoming assigned visits (offboarding sheet).
  technicianUpcomingVisits: (technicianId: string) =>
    [...schedulerKeys.all(), 'technicians', technicianId, 'upcoming-visits'] as const,
  crews: () => [...schedulerKeys.all(), 'crews'] as const,
  teams: () => [...schedulerKeys.all(), 'teams'] as const,
  serviceTypes: () => [...schedulerKeys.all(), 'serviceTypes'] as const,
  trades: (status?: string) => [...schedulerKeys.all(), 'trades', status ?? 'all'] as const,
  // Provisioner trade catalog per tenant.
  tradeCatalog: () => [...schedulerKeys.all(), 'tradeCatalog'] as const,
  // NOT-542 — Business Units list, keyed by status filter.
  businessUnits: (status?: string) =>
    [...schedulerKeys.all(), 'businessUnits', status ?? 'all'] as const,
  oncalls: () => [...schedulerKeys.all(), 'oncalls'] as const,
  coverage: (zipCode: string, serviceTypeId: string | null) =>
    [...schedulerKeys.all(), 'coverage', zipCode, serviceTypeId ?? 'any'] as const,
  // Scheduling v2 phase 4 — ranked route-day candidates for moving a job.
  insertionCandidates: (jobId: string) =>
    [...schedulerKeys.all(), 'insertionCandidates', jobId] as const,
  // §9 — create-form variant: candidates ranked by a typed address (no jobId).
  insertionCandidatesByAddress: (key: string) =>
    [...schedulerKeys.all(), 'insertionCandidatesByAddress', key] as const,
  recurringRoutePlan: (key: string) => [...schedulerKeys.all(), 'recurringRoutePlan', key] as const,
  // Scheduling v2 phase 4 — nightly route-efficiency snapshots.
  routeDrift: (range: { from: string; to: string; technicianId?: string }) =>
    [...schedulerKeys.all(), 'routeDrift', range] as const,
};

// ─── Tax Rates (operations service) ──────────────────────────────────────────

export const taxRateKeys = {
  all: () => ['taxRates', getTenantId()] as const,
  lists: () => [...taxRateKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...taxRateKeys.lists(), params] as const,
  details: () => [...taxRateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...taxRateKeys.details(), id] as const,
  lookup: (params: Record<string, unknown>) => [...taxRateKeys.all(), 'lookup', params] as const,
};

// ─── AI Conversations ─────────────────────────────────────────────────────────

export const aiConversationKeys = {
  all: () => ['aiConversations', getTenantId()] as const,
  lists: () => [...aiConversationKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...aiConversationKeys.lists(), params] as const,
  details: () => [...aiConversationKeys.all(), 'detail'] as const,
  detail: (id: string) => [...aiConversationKeys.details(), id] as const,
  messages: (id: string) => [...aiConversationKeys.detail(id), 'messages'] as const,
};

// ─── AI Escalations ───────────────────────────────────────────────────────────

export const aiEscalationKeys = {
  all: () => ['aiEscalations', getTenantId()] as const,
  lists: () => [...aiEscalationKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...aiEscalationKeys.lists(), params] as const,
  details: () => [...aiEscalationKeys.all(), 'detail'] as const,
  detail: (id: string) => [...aiEscalationKeys.details(), id] as const,
};

// ─── Lead Scoring (AI) ────────────────────────────────────────────────────────

export const leadScoringKeys = {
  all: () => ['leadScoring', getTenantId()] as const,
  score: (leadId: string) => [...leadScoringKeys.all(), 'score', leadId] as const,
};

// ─── Audit Logs ───────────────────────────────────────────────────────────────

export const auditLogKeys = {
  all: () => ['auditLogs', getTenantId()] as const,
  lists: () => [...auditLogKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...auditLogKeys.lists(), params] as const,
  details: () => [...auditLogKeys.all(), 'detail'] as const,
  detail: (id: string) => [...auditLogKeys.details(), id] as const,
  byEntity: (entityType: string, entityId: string) =>
    [...auditLogKeys.all(), 'entity', entityType, entityId] as const,
};

// ─── Promotions (operations microservice) ────────────────────────────────────

export const promotionKeys = {
  all: () => ['promotions', getTenantId()] as const,
  lists: () => [...promotionKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...promotionKeys.lists(), params] as const,
  details: () => [...promotionKeys.all(), 'detail'] as const,
  detail: (id: string) => [...promotionKeys.details(), id] as const,
};

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const dashboardKeys = {
  all: () => ['dashboard', getTenantId()] as const,
  summary: () => [...dashboardKeys.all(), 'summary'] as const,
  graph: (params: ListParams) => [...dashboardKeys.all(), 'graph', params] as const,
  stats: (period?: string) => [...dashboardKeys.all(), 'stats', period ?? 'this_week'] as const,
  revenue: (period?: string) => [...dashboardKeys.all(), 'revenue', period ?? 'this_week'] as const,
  revenueChart: (period?: string) =>
    [...dashboardKeys.all(), 'revenueChart', period ?? 'this_week'] as const,
  recentActivity: () => [...dashboardKeys.all(), 'recentActivity'] as const,
  upcomingJobs: () => [...dashboardKeys.all(), 'upcomingJobs'] as const,
  todayBoard: (day: string) => [...dashboardKeys.all(), 'todayBoard', day] as const,
  actionNeeded: (period?: string) =>
    [...dashboardKeys.all(), 'actionNeeded', period ?? 'this_week'] as const,
  pipelineStats: (period?: string) =>
    [...dashboardKeys.all(), 'pipelineStats', period ?? 'this_week'] as const,
};

// ─── Estimates (Operations) ──────────────────────────────────────────────────

export const estimateKeys = {
  all: () => ['estimates', getTenantId()] as const,
  lists: () => [...estimateKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...estimateKeys.lists(), params] as const,
  nextNumber: () => [...estimateKeys.all(), 'next-number'] as const,
  details: () => [...estimateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...estimateKeys.details(), id] as const,
  attachments: (id: string) => [...estimateKeys.detail(id), 'attachments'] as const,
  attachmentImages: (id: string) => [...estimateKeys.attachments(id), 'images'] as const,
  pdfDownloadUrl: (id: string, pdfId?: string) =>
    [...estimateKeys.detail(id), 'pdf', 'download-url', pdfId ?? 'active'] as const,
  documentLayout: (id: string, previewDesign?: string) =>
    [...estimateKeys.detail(id), 'document-layout', previewDesign ?? 'saved'] as const,
  // Analytics reports
  reports: () => [...estimateKeys.all(), 'reports'] as const,
  report: (
    name: string,
    period?: {
      from?: string;
      to?: string;
      ownerId?: string;
      technicianId?: string;
    },
  ) =>
    [
      ...estimateKeys.reports(),
      name,
      period?.from ?? '',
      period?.to ?? '',
      period?.ownerId ?? '',
      period?.technicianId ?? '',
    ] as const,
};

export const entityTypeKeys = {
  all: () => ['entity-types'] as const,
};

export const tagKeys = {
  all: () => ['tags', getTenantId()] as const,
  lists: (scope: string) => [...tagKeys.all(), 'list', scope] as const,
  detail: (id: string) => [...tagKeys.all(), 'detail', id] as const,
};

export const opsProposalKeys = {
  all: () => ['opsProposals', getTenantId()] as const,
  lists: () => [...opsProposalKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...opsProposalKeys.lists(), params] as const,
  details: () => [...opsProposalKeys.all(), 'detail'] as const,
  detail: (id: string) => [...opsProposalKeys.details(), id] as const,
  /** The customer-facing projection shown in presentation mode. */
  presentation: (id: string) => [...opsProposalKeys.details(), id, 'presentation'] as const,
};

export const estimateTemplateKeys = {
  all: () => ['estimateTemplates', getTenantId()] as const,
  lists: () => [...estimateTemplateKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...estimateTemplateKeys.lists(), params] as const,
  details: () => [...estimateTemplateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...estimateTemplateKeys.details(), id] as const,
  attachments: (id: string) => [...estimateTemplateKeys.detail(id), 'attachments'] as const,
};

export const estimateDocTemplateKeys = {
  all: () => ['estimateDocTemplates', getTenantId()] as const,
  lists: () => [...estimateDocTemplateKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...estimateDocTemplateKeys.lists(), params] as const,
  details: () => [...estimateDocTemplateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...estimateDocTemplateKeys.details(), id] as const,
  // Layout of a PICKED design for a not-yet-saved estimate (create-flow
  // preview). Carries the 'document-layout' segment so the existing
  // resolved-layout invalidation predicate (publish/update/default/delete)
  // sweeps it too. `previewDesign` = 'default' | template id.
  designPreviewLayout: (previewDesign: string, businessUnitId?: string | null, docType?: string) =>
    [
      ...estimateDocTemplateKeys.all(),
      'document-layout',
      'design-preview',
      previewDesign,
      businessUnitId ?? 'no-bu',
      docType ?? 'ESTIMATE',
    ] as const,
};

export const emailTemplateKeys = {
  all: () => ['emailTemplates', getTenantId()] as const,
  lists: () => [...emailTemplateKeys.all(), 'list'] as const,
  list: (params: { kind?: string } = {}) => [...emailTemplateKeys.lists(), params] as const,
  details: () => [...emailTemplateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...emailTemplateKeys.details(), id] as const,
  businessUnits: (id: string) => [...emailTemplateKeys.detail(id), 'business-units'] as const,
  starters: (kind?: string) => [...emailTemplateKeys.all(), 'starters', kind ?? 'all'] as const,
};

export const proposalTemplateKeys = {
  all: () => ['proposalTemplates', getTenantId()] as const,
  lists: () => [...proposalTemplateKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...proposalTemplateKeys.lists(), params] as const,
  details: () => [...proposalTemplateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...proposalTemplateKeys.details(), id] as const,
  attachments: (id: string) => [...proposalTemplateKeys.detail(id), 'attachments'] as const,
};

// ─── Jobs ────────────────────────────────────────────────────────────────────

export const jobCostKeys = {
  all: () => ['jobCosts', getTenantId()] as const,
  summary: (jobId: string) => [...jobCostKeys.all(), 'summary', jobId] as const,
};

export const productionKitKeys = {
  all: () => ['productionKits', getTenantId()] as const,
  list: () => [...productionKitKeys.all(), 'list'] as const,
  detail: (id: string) => [...productionKitKeys.all(), 'detail', id] as const,
};

export const jobKeys = {
  all: () => ['jobs', getTenantId()] as const,
  lists: () => [...jobKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...jobKeys.lists(), params] as const,
  details: () => [...jobKeys.all(), 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
  equipment: (id: string) => [...jobKeys.detail(id), 'equipment'] as const,
  financialSummary: (id: string) => [...jobKeys.detail(id), 'financial-summary'] as const,
  appointments: (id: string) => [...jobKeys.detail(id), 'appointments'] as const,
  appointment: (id: string, appointmentId: string) =>
    [...jobKeys.appointments(id), appointmentId] as const,
  // GAP-03 — projected, not-yet-materialised recurring occurrences.
  upcomingVisits: (id: string) => [...jobKeys.detail(id), 'upcomingVisits'] as const,
  // NOT-339 §9 — per-visit review status for the recurring Job detail.
  reviews: (id: string) => [...jobKeys.detail(id), 'reviews'] as const,
  payments: (id: string) => [...jobKeys.detail(id), 'payments'] as const,
  related: (id: string) => [...jobKeys.detail(id), 'related'] as const,
  // NOT-573 media roll-up (params: visitId / jobLevel filters).
  mediaBase: (id: string) => [...jobKeys.detail(id), 'media'] as const,
  media: (id: string, params: Record<string, unknown> = {}) =>
    [...jobKeys.mediaBase(id), params] as const,
  activity: (id: string) => [...jobKeys.detail(id), 'activity'] as const,
  // Analytics reports
  reports: () => [...jobKeys.all(), 'reports'] as const,
  report: (
    name: string,
    period?: {
      from?: string;
      to?: string;
      ownerId?: string;
      technicianId?: string;
      jobTypeId?: string;
      lowVolumeThreshold?: number;
    },
  ) =>
    [
      ...jobKeys.reports(),
      name,
      period?.from ?? '',
      period?.to ?? '',
      period?.ownerId ?? '',
      period?.technicianId ?? '',
      period?.jobTypeId ?? '',
      period?.lowVolumeThreshold ?? '',
    ] as const,
};

/**
 * Matches a cached job-appointments LIST or single-appointment DETAIL query, but
 * NOT deeper appointment subresources (e.g. `appointmentAttachments`). Used to
 * invalidate appointment-scoped auth / sign-off mutations that only carry the
 * appointment id. Only one job's appointments are realistically cached on these
 * pages, so matching across jobs is harmless; excluding subresources avoids
 * refetch churn for unrelated appointment data (and stays correct as new
 * subresources are added).
 */
export function isJobAppointmentsQuery(query: { queryKey: readonly unknown[] }): boolean {
  const key = query.queryKey;
  if (key[0] !== 'jobs') return false;
  const i = key.indexOf('appointments');
  // List ends at 'appointments' (length i+1); single-appointment detail is one
  // segment further (i+2). Anything deeper is a subresource — don't match it.
  return i >= 0 && key.length <= i + 2;
}

// ─── Job Pipelines ───────────────────────────────────────────────────────────

export const jobPipelineKeys = {
  all: () => ['jobPipelines', getTenantId()] as const,
  lists: () => [...jobPipelineKeys.all(), 'list'] as const,
  details: () => [...jobPipelineKeys.all(), 'detail'] as const,
  detail: (id: string) => [...jobPipelineKeys.details(), id] as const,
};

// ─── Time Entries ────────────────────────────────────────────────────────────

export const timeEntryKeys = {
  all: () => ['timeEntries', getTenantId()] as const,
  appointment: (appointmentId: string) =>
    [...timeEntryKeys.all(), 'appointment', appointmentId] as const,
  detail: (id: string) => [...timeEntryKeys.all(), 'detail', id] as const,
  jobRollup: (jobId: string) => [...timeEntryKeys.all(), 'jobRollup', jobId] as const,
};

// ─── Expenses ────────────────────────────────────────────────────────────────

export const expenseKeys = {
  all: () => ['expenses', getTenantId()] as const,
  byJob: (jobId: string, params?: ListParams) =>
    params
      ? ([...expenseKeys.all(), 'job', jobId, params] as const)
      : ([...expenseKeys.all(), 'job', jobId] as const),
  profitability: (jobId: string) => [...expenseKeys.all(), 'profitability', jobId] as const,
  detail: (id: string) => [...expenseKeys.all(), 'detail', id] as const,
  attachments: (id: string) => [...expenseKeys.detail(id), 'attachments'] as const,
};

// ─── Payment Schedules (spec §5) ────────────────────────────────────────────

export const paymentScheduleKeys = {
  all: () => ['paymentSchedules', getTenantId()] as const,
  detail: (id: string) => [...paymentScheduleKeys.all(), 'detail', id] as const,
  byJob: (jobId: string) => [...paymentScheduleKeys.all(), 'job', jobId] as const,
};

// ─── Consent Records (spec §5.5) ────────────────────────────────────────────

export const consentKeys = {
  all: () => ['consentRecords', getTenantId()] as const,
  byContact: (contactId: string, activeOnly?: boolean) =>
    [...consentKeys.all(), 'contact', contactId, activeOnly ?? null] as const,
};

// ─── Tax Zones & Groups ─────────────────────────────────────────────────────

export const taxZoneKeys = {
  all: () => ['taxZones', getTenantId()] as const,
  list: () => [...taxZoneKeys.all(), 'list'] as const,
  summary: () => [...taxZoneKeys.all(), 'summary'] as const,
  // NOT-130 — flat list of every active override across the tenant.
  overrides: () => [...taxZoneKeys.all(), 'overrides'] as const,
};

export const taxGroupKeys = {
  all: () => ['taxGroups', getTenantId()] as const,
  list: () => [...taxGroupKeys.all(), 'list'] as const,
  detail: (id: string) => [...taxGroupKeys.all(), 'detail', id] as const,
};

export const taxPolicyKeys = {
  all: () => ['taxPolicies', getTenantId()] as const,
  list: () => [...taxPolicyKeys.all(), 'list'] as const,
  documentInput: (kind: string, id: string) =>
    [...taxPolicyKeys.all(), 'document-input', kind, id] as const,
};

// ─── Service Areas ──────────────────────────────────────────────────────────

export const serviceAreaKeys = {
  all: () => ['serviceAreas', getTenantId()] as const,
  list: () => [...serviceAreaKeys.all(), 'list'] as const,
  coverage: () => [...serviceAreaKeys.all(), 'coverage'] as const,
  placeSearch: (q: string) => [...serviceAreaKeys.all(), 'place-search', q] as const,
};

export const onboardingKeys = {
  all: () => ['onboarding', getTenantId()] as const,
  status: () => [...onboardingKeys.all(), 'status'] as const,
};

export const territoryKeys = {
  all: () => ['territories', getTenantId()] as const,
  list: () => [...territoryKeys.all(), 'list'] as const,
  tree: () => [...territoryKeys.all(), 'tree'] as const,
  zips: (id: string) => [...territoryKeys.all(), 'zips', id] as const,
  techs: (id: string) => [...territoryKeys.all(), 'techs', id] as const,
};

export const capacityRuleKeys = {
  all: () => ['capacityRules', getTenantId()] as const,
  list: () => [...capacityRuleKeys.all(), 'list'] as const,
};

// ─── Memberships ────────────────────────────────────────────────────────────

// ─── Custom Fields (operations — define-once, associate-many) ───────────────

export const customFieldKeys = {
  all: () => ['customFields', getTenantId()] as const,
  definitions: () => [...customFieldKeys.all(), 'definitions'] as const,
  definition: (id: string) => [...customFieldKeys.definitions(), id] as const,
  // NOT-369 — the job-type segment must distinguish three states that
  // `jobTypeId ?? null` collapsed into one (and rendered another type's fields
  // on an untyped job): `undefined` = "every association" (settings/create, no
  // job type filter) → 'all'; `null | ''` = "untyped job, unscoped only" →
  // 'untyped'; otherwise the job type id itself.
  schema: (entityType: string, includeInactiveDefinitions = false, jobTypeId?: string | null) =>
    [
      ...customFieldKeys.all(),
      'schema',
      entityType,
      includeInactiveDefinitions,
      jobTypeId === undefined
        ? 'all'
        : jobTypeId == null || jobTypeId === ''
          ? 'untyped'
          : jobTypeId,
    ] as const,
  associations: (defId: string) => [...customFieldKeys.all(), 'associations', defId] as const,
  // NOT-369 — JOB values are scoped server-side by `entityId` (the job id), so
  // no job-type segment: one key per entity. Keeps `useUpsertValues`
  // invalidation (which knows only entityType+entityId) in lock-step.
  values: (entityType: string, entityId: string) =>
    [...customFieldKeys.all(), 'values', entityType, entityId] as const,
  bulkValues: (entityType: string, entityIds: string[]) =>
    [...customFieldKeys.all(), 'values', entityType, 'bulk', entityIds] as const,
  /** Prefix of both `values` and `bulkValues` for a type — invalidate to refresh the
   *  single detail cache AND the table/bulk caches after a value write. */
  valuesBase: (entityType: string) => [...customFieldKeys.all(), 'values', entityType] as const,
  filter: (entityType: string, filter: object | null) =>
    [...customFieldKeys.all(), 'filter', entityType, filter] as const,
  inheritanceRules: () => [...customFieldKeys.all(), 'inheritance'] as const,
};

// ─── Reminder Policies (operations v2) ──────────────────────────────────────

export const reminderPolicyKeys = {
  all: () => ['reminderPolicies', getTenantId()] as const,
  lists: () => [...reminderPolicyKeys.all(), 'list'] as const,
  list: () => [...reminderPolicyKeys.lists()] as const,
  details: () => [...reminderPolicyKeys.all(), 'detail'] as const,
  detail: (type: string) => [...reminderPolicyKeys.details(), type] as const,
};

// ─── Memberships ────────────────────────────────────────────────────────────

export const membershipKeys = {
  all: () => ['memberships', getTenantId()] as const,
  lists: () => [...membershipKeys.all(), 'list'] as const,
  list: (params: ListParams) => [...membershipKeys.lists(), params] as const,
  details: () => [...membershipKeys.all(), 'detail'] as const,
  detail: (id: string) => [...membershipKeys.details(), id] as const,
  reports: () => [...membershipKeys.all(), 'reports'] as const,
  report: (name: string, period?: { from?: string; to?: string; ownerId?: string }) =>
    [
      ...membershipKeys.reports(),
      name,
      period?.from ?? '',
      period?.to ?? '',
      period?.ownerId ?? '',
    ] as const,
  jobOpportunity: (jobId: string) => [...membershipKeys.all(), 'job-opportunity', jobId] as const,
};

// ─── Review Platforms (operations v2) ───────────────────────────────────────

export const companyCamKeys = {
  all: () => ['companycam', getTenantId()] as const,
  /** GET /v2/integrations/companycam/status */
  status: () => [...companyCamKeys.all(), 'status'] as const,
  /** GET /v2/integrations/companycam/settings */
  settings: () => [...companyCamKeys.all(), 'settings'] as const,
};

export const reviewPlatformKeys = {
  all: () => ['reviewPlatforms', getTenantId()] as const,
  /** GET /v2/review-platforms/settings (tenant-wide or per-location). */
  settings: (locationId: string | null = null) =>
    [...reviewPlatformKeys.all(), 'settings', locationId ?? 'tenant'] as const,
  /** GET /v2/review-platforms/oauth/:platform/available-businesses?oauthTokenId=... */
  availableBusinesses: (platform: string, oauthTokenId: string) =>
    [...reviewPlatformKeys.all(), 'oauth', platform, 'businesses', oauthTokenId] as const,
};

// ─── Webhook Subscriptions (operations) ─────────────────────────────────────

export const webhookSubscriptionKeys = {
  all: (surface: 'generic' | 'partner' = 'generic') =>
    ['webhookSubscriptions', getTenantId(), surface] as const,
  events: (surface: 'generic' | 'partner' = 'generic') =>
    [...webhookSubscriptionKeys.all(surface), 'events'] as const,
  lists: (surface: 'generic' | 'partner' = 'generic') =>
    [...webhookSubscriptionKeys.all(surface), 'list'] as const,
  detail: (id: string, surface: 'generic' | 'partner' = 'generic') =>
    [...webhookSubscriptionKeys.all(surface), 'detail', id] as const,
  deliveries: (
    id: string,
    page: number,
    limit: number,
    surface: 'generic' | 'partner' = 'generic',
  ) => [...webhookSubscriptionKeys.detail(id, surface), 'deliveries', page, limit] as const,
};

// ─── Job Reports (spec §12) ─────────────────────────────────────────────────

export const reportKeys = {
  all: () => ['reports', getTenantId()] as const,
  list: (filters: Record<string, unknown> = {}) => [...reportKeys.all(), 'list', filters] as const,
  detail: (id: string) => [...reportKeys.all(), 'detail', id] as const,
  revisions: (id: string) => [...reportKeys.all(), 'revisions', id] as const,
  fieldCatalog: () => [...reportKeys.all(), 'field-catalog'] as const,
  fields: (id: string) => [...reportKeys.all(), 'fields', id] as const,
  shareInfo: (id: string) => [...reportKeys.all(), 'share-info', id] as const,
  accessLog: (id: string) => [...reportKeys.all(), 'access-log', id] as const,
  photos: (id: string) => [...reportKeys.all(), 'photos', id] as const,
  forJob: (jobId: string) => [...reportKeys.all(), 'for-job', jobId] as const,
};

export const reportTemplateKeys = {
  all: () => ['reportTemplates', getTenantId()] as const,
  list: () => [...reportTemplateKeys.all(), 'list'] as const,
  detail: (id: string) => [...reportTemplateKeys.all(), 'detail', id] as const,
};

export const reportSettingsKeys = {
  all: () => ['reportSettings', getTenantId()] as const,
};

// ─── Messaging v2 (operations) ──────────────────────────────────────────────

export const messagingKeys = {
  all: () => ['messaging', getTenantId()] as const,
  threads: () => [...messagingKeys.all(), 'threads'] as const,
  threadList: (params: ListParams = {}) => [...messagingKeys.threads(), 'list', params] as const,
  thread: (id: string) => [...messagingKeys.threads(), 'detail', id] as const,
  threadMessages: (id: string, params: ListParams = {}) =>
    [...messagingKeys.thread(id), 'messages', params] as const,
  phoneNumbers: () => [...messagingKeys.all(), 'phoneNumbers'] as const,
  phoneNumberList: (params: ListParams = {}) =>
    [...messagingKeys.phoneNumbers(), 'list', params] as const,
  phoneNumber: (id: string) => [...messagingKeys.phoneNumbers(), 'detail', id] as const,
  availableNumbers: (params: ListParams = {}) =>
    [...messagingKeys.phoneNumbers(), 'available', params] as const,
  groupMessagingOptions: () => [...messagingKeys.all(), 'groupMessaging', 'options'] as const,
  inboxes: () => [...messagingKeys.all(), 'inboxes'] as const,
  channelAccess: (channelId: string) =>
    [...messagingKeys.all(), 'channelAccess', channelId] as const,
  webchatWidgets: () => [...messagingKeys.all(), 'webchatWidgets'] as const,
  webchatWidget: (id: string) => [...messagingKeys.webchatWidgets(), id] as const,
  webchatEmbed: (id: string) => [...messagingKeys.webchatWidgets(), id, 'embed'] as const,
};

// ─── Call logs (NOT-15 — proxied from the calling service) ──────────────────

export const callLogKeys = {
  all: () => ['callLogs', getTenantId()] as const,
  list: (params: ListParams = {}) => [...callLogKeys.all(), 'list', params] as const,
  /** The Call log's scrolling list. Under `list` so whatever refreshes the
   *  paged lists refreshes this too; its own segment so the two shapes
   *  (one page vs. `{ pages }`) never share a cache entry. */
  infiniteList: (params: ListParams = {}) =>
    [...callLogKeys.all(), 'list', 'infinite', params] as const,
  detail: (callControlId: string) => [...callLogKeys.all(), 'detail', callControlId] as const,
  report: (params: ListParams = {}) => [...callLogKeys.all(), 'report', params] as const,
  /** The Reports page's one payload (GET /call-logs/reports). */
  reports: (params: ListParams = {}) => [...callLogKeys.all(), 'reports', params] as const,
  unbooked: (params: ListParams = {}) => [...callLogKeys.all(), 'unbooked', params] as const,
  // One root for the queue so a resolve/assign/attempt can invalidate every
  // tab at once — the counts on the other two move when this one changes.
  actionNeeded: () => [...callLogKeys.all(), 'action-needed'] as const,
  followUpAssignees: () => [...callLogKeys.all(), 'action-needed-assignees'] as const,
  actionNeededList: (params: ListParams = {}) => [...callLogKeys.actionNeeded(), params] as const,
  /** The queue's scrolling list — under the queue root, so every resolve,
   *  assign and attempt that invalidates the queue reaches it. */
  actionNeededInfinite: (params: ListParams = {}) =>
    [...callLogKeys.actionNeeded(), 'infinite', params] as const,
  /** Keyed on the whole filter set: changing any filter is a new question. */
  actionNeededReport: (params: ListParams = {}) =>
    [...callLogKeys.actionNeeded(), 'report', params] as const,
  // Under the queue's root on purpose: adding a note or filing an outcome
  // changes the row's summary in the grid, so one invalidation covers both.
  followUp: (callControlId: string) =>
    [...callLogKeys.actionNeeded(), 'row', callControlId] as const,
  followUpActivity: (callControlId: string) =>
    [...callLogKeys.actionNeeded(), 'activity', callControlId] as const,
  // Iris's reading of a call. Outside the queue root — it depends on the call,
  // not on the row's state, so clearing the queue must not refetch it.
  outcomeSuggestion: (callControlId: string) =>
    [...callLogKeys.all(), 'outcome-suggestion', callControlId] as const,
};

// ─── Call outcomes (the tenant's own vocabulary) ────────────────────────────

export const callOutcomeKeys = {
  all: () => ['callOutcomes', getTenantId()] as const,
  list: () => [...callOutcomeKeys.all(), 'list'] as const,
};

// ─── Voice / calling settings (agent calling flags) ─────────────────────────

export const voiceKeys = {
  all: () => ['voice', getTenantId()] as const,
  agents: () => [...voiceKeys.all(), 'agents'] as const,
  automation: () => [...voiceKeys.all(), 'automation'] as const,
  receptionist: () => [...voiceKeys.all(), 'receptionist'] as const,
  ivr: () => [...voiceKeys.all(), 'ivr'] as const,
  directory: () => [...voiceKeys.all(), 'directory'] as const,
  numbers: () => [...voiceKeys.all(), 'numbers'] as const,
  landlines: () => [...voiceKeys.all(), 'landlines'] as const,
  landlineRegistration: () => [...voiceKeys.all(), 'landline-registration'] as const,
  accountSchedule: () => [...voiceKeys.all(), 'account-schedule'] as const,
  ivrAnalytics: (range: { from?: string; to?: string } = {}) =>
    [...voiceKeys.all(), 'ivr-analytics', range] as const,
  callFlows: () => [...voiceKeys.all(), 'call-flows'] as const,
  callFlow: (id: string | undefined) => [...voiceKeys.callFlows(), id] as const,
  // Per user, not just per tenant: the value is the signed-in person's own
  // setting, and it is cached for the whole page load.
  doNotDisturb: (userId: string | null) => [...voiceKeys.all(), 'do-not-disturb', userId] as const,
};

// ─── Recurring Service & Contracts v1.1 (spec §13–§15) ──────────────────────
// NOT-339 §3.3 — `contractKeys` removed with the retired Contract aggregate;
// recurring data is keyed under `jobKeys`. The settings panel keeps its own.

export const contractSettingsKeys = {
  all: () => ['contractSettings', getTenantId()] as const,
};

// ─── Payment Schedule Templates (spec §9) ───────────────────────────────────

export const paymentScheduleTemplateKeys = {
  all: () => ['paymentScheduleTemplates', getTenantId()] as const,
  forEstimate: (estimateId: string) =>
    [...paymentScheduleTemplateKeys.all(), 'estimate', estimateId] as const,
  preview: (estimateId: string) =>
    [...paymentScheduleTemplateKeys.all(), 'preview', estimateId] as const,
};

export const membershipPlanKeys = {
  all: () => ['membershipPlans', getTenantId()] as const,
  lists: () => [...membershipPlanKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...membershipPlanKeys.lists(), params] as const,
  details: () => [...membershipPlanKeys.all(), 'detail'] as const,
  detail: (id: string) => [...membershipPlanKeys.details(), id] as const,
  pricingContext: (id: string) => [...membershipPlanKeys.all(), 'pricing-context', id] as const,
  detailStats: (id: string) => [...membershipPlanKeys.detail(id), 'stats'] as const,
  recurringServices: (planId: string) =>
    [...membershipPlanKeys.detail(planId), 'recurring-services'] as const,
};

export const timeOffKeys = {
  all: () => ['timeOff', getTenantId()] as const,
  list: (technicianId: string) => [...timeOffKeys.all(), 'list', technicianId] as const,
};

export const payrollProfileKeys = {
  all: () => ['payrollProfiles', getTenantId()] as const,
  lists: () => [...payrollProfileKeys.all(), 'list'] as const,
  list: (params: ListParams = {}) => [...payrollProfileKeys.lists(), params] as const,
  details: () => [...payrollProfileKeys.all(), 'detail'] as const,
  detail: (id: string) => [...payrollProfileKeys.details(), id] as const,
};

export const paymentSchedulePresetKeys = {
  all: () => ['paymentSchedulePresets', getTenantId()] as const,
  list: () => [...paymentSchedulePresetKeys.all(), 'list'] as const,
};

// ─── Recurring Service & Contracts: recurrence presets (spec §15) ───────────

export const recurrencePresetKeys = {
  all: () => ['recurrencePresets', getTenantId()] as const,
  list: () => [...recurrencePresetKeys.all(), 'list'] as const,
};

// ─── Scheduling v2 — policy + arrival-window presets ────────────────────────

export const schedulingV2Keys = {
  all: () => ['schedulingV2', getTenantId()] as const,
  policy: () => [...schedulingV2Keys.all(), 'policy'] as const,
  bookingPolicy: (jobTypeId?: string | null) =>
    [...schedulingV2Keys.policy(), 'booking', jobTypeId ?? null] as const,
  arrivalWindowLists: () => [...schedulingV2Keys.all(), 'arrivalWindows'] as const,
  arrivalWindows: (params: ListParams = {}) =>
    [...schedulingV2Keys.arrivalWindowLists(), params] as const,
  policyOverrides: () => [...schedulingV2Keys.all(), 'policyOverrides'] as const,
};
